import React, { useState } from 'react';
import { Button, Card, CardContent } from '@/components/ui';
import { AuditViewer } from '@/components/audit';
import { toISODatetime } from '@/lib/utils';
import type { InventoryLineItem, UpdateLineItemData } from '@/types';
import LineItemRow from './LineItemRow';

interface LineItemListProps {
  lineItems: InventoryLineItem[];
  onUpdate: (id: string, data: UpdateLineItemData) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onBulkUpdate?: (lineItemIds: string[], updates: UpdateLineItemData) => Promise<void>;
  isLoading?: boolean;
  businessCategory?: string | undefined;
  canEdit?: boolean;
  readOnlyHoldPosition?: boolean; // New prop to make hold position read-only
}

const LINE_ITEMS_PER_PAGE = 10;

export default function LineItemList({
  lineItems,
  onUpdate,
  onDelete,
  onBulkUpdate,
  isLoading = false,
  businessCategory,
  canEdit = true,
  readOnlyHoldPosition = false,
}: LineItemListProps) {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState<UpdateLineItemData>({});
  const [viewingHistoryId, setViewingHistoryId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Sort line items by creation date (oldest first, most recent at bottom)
  const sortedLineItems = React.useMemo(() => {
    return [...lineItems].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateA - dateB;
    });
  }, [lineItems]);

  // Pagination
  const totalPages = Math.ceil(sortedLineItems.length / LINE_ITEMS_PER_PAGE);
  const showPagination = sortedLineItems.length > LINE_ITEMS_PER_PAGE;
  const paginatedLineItems = React.useMemo(() => {
    const start = (currentPage - 1) * LINE_ITEMS_PER_PAGE;
    return sortedLineItems.slice(start, start + LINE_ITEMS_PER_PAGE);
  }, [sortedLineItems, currentPage]);

  // Reset to page 1 when line items change (e.g., after add/delete)
  React.useEffect(() => {
    const maxPage = Math.ceil(sortedLineItems.length / LINE_ITEMS_PER_PAGE) || 1;
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [sortedLineItems.length, currentPage]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all items on the current page
      const pageItemIds = paginatedLineItems.map(item => item.id);
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        pageItemIds.forEach(id => newSet.add(id));
        return newSet;
      });
    } else {
      // Deselect all items on the current page
      const pageItemIds = new Set(paginatedLineItems.map(item => item.id));
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        pageItemIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  };

  const handleSelectItem = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedItems(newSelected);
  };

  const handleBulkUpdate = async () => {
    if (onBulkUpdate && selectedItems.size > 0) {
      // Normalize date values before sending:
      // - Plain YYYY-MM-DD from input[type=date] → append T00:00:00.000Z
      // - Already-ISO strings (contain T) → pass through unchanged
      // - Empty string (user cleared the field) → undefined so Zod skips the field
      const normalizedStart = toISODatetime(bulkUpdateData.flightStartDate);
      const normalizedEnd = toISODatetime(bulkUpdateData.flightEndDate);

      const dataToSend: UpdateLineItemData = {
        ...bulkUpdateData,
        flightStartDate: normalizedStart,
        flightEndDate: normalizedEnd,
      };
      await onBulkUpdate(Array.from(selectedItems), dataToSend);
      // Only clear on success — if onBulkUpdate throws, selection stays open for retry
      setSelectedItems(new Set());
      setShowBulkUpdate(false);
      setBulkUpdateData({});
    }
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async (id: string, data: UpdateLineItemData) => {
    try {
      await onUpdate(id, data);
      setEditingId(null); // only exit edit mode on success
    } catch {
      // error is shown via the parent's updateError banner — keep row in edit mode
    }
  };

  const handleViewHistory = (id: string) => {
    setViewingHistoryId(viewingHistoryId === id ? null : id);
  };

  const allPageSelected = paginatedLineItems.length > 0 && paginatedLineItems.every(item => selectedItems.has(item.id));
  const somePageSelected = paginatedLineItems.some(item => selectedItems.has(item.id)) && !allPageSelected;

  if (sortedLineItems.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-center text-neutral-400">No line items yet. Add your first line item to get started.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Bulk Actions Bar */}
      {canEdit && selectedItems.size > 0 && (
        <Card className="bg-outfront-purple-light border-outfront-purple">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-outfront-purple">
                  {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowBulkUpdate(!showBulkUpdate)}
                >
                  Bulk Update Dates
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedItems(new Set())}
                >
                  Clear Selection
                </Button>
              </div>
            </div>

            {/* Bulk Update Form */}
            {showBulkUpdate && (
              <div className="mt-4 p-4 bg-white rounded-md border border-outfront-purple">
                <h4 className="text-sm font-semibold text-neutral-900 mb-3">Update Selected Items</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-600 mb-1">
                      Flight Start Date
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm"
                      value={bulkUpdateData.flightStartDate?.split('T')[0] || ''}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, flightStartDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-600 mb-1">
                      Flight End Date
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-md border border-neutral-200 px-3 py-2 text-sm"
                      value={bulkUpdateData.flightEndDate?.split('T')[0] || ''}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, flightEndDate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowBulkUpdate(false);
                      setBulkUpdateData({});
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => { handleBulkUpdate().catch(() => { /* error shown via parent banner */ }); }}
                    isLoading={isLoading}
                    disabled={Object.keys(bulkUpdateData).length === 0}
                  >
                    Apply to {selectedItems.size} Item{selectedItems.size !== 1 ? 's' : ''}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Line Items Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                {canEdit && (
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-neutral-200 text-primary-600 focus:ring-primary-500"
                      checked={allPageSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = somePageSelected;
                        }
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                  Display Name
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-neutral-400 uppercase tracking-wider">
                  Qty / Spots
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                  Hold Expiration
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                  Flight Dates
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                  Hold Position
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                  Showing Type
                </th>
                {canEdit && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {paginatedLineItems.map((item) => (
                <React.Fragment key={item.id}>
                  <LineItemRow
                    item={item}
                    isSelected={selectedItems.has(item.id)}
                    isEditing={editingId === item.id}
                    onSelect={(checked) => handleSelectItem(item.id, checked)}
                    onEdit={() => handleEdit(item.id)}
                    onCancelEdit={handleCancelEdit}
                    onSave={(data) => handleSaveEdit(item.id, data)}
                    onDelete={() => onDelete(item.id)}
                    onViewHistory={() => handleViewHistory(item.id)}
                    businessCategory={businessCategory}
                    isLoading={isLoading}
                    canEdit={canEdit}
                    readOnlyHoldPosition={readOnlyHoldPosition}
                  />
                  {viewingHistoryId === item.id && (
                    <tr>
                      <td colSpan={canEdit ? 8 : 7} className="px-4 py-4 bg-neutral-50">
                        <AuditViewer
                          tableName="inventory_line_items"
                          recordId={item.id}
                          title={`Change History - ${item.displayName}`}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {showPagination && (
          <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-600">
                Page {currentPage} of {totalPages}
              </span>
              <span className="text-xs text-neutral-400">
                ({(currentPage - 1) * LINE_ITEMS_PER_PAGE + 1}–{Math.min(currentPage * LINE_ITEMS_PER_PAGE, sortedLineItems.length)} of {sortedLineItems.length} items)
              </span>
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </Card>
    </div>
  );
}

